// Sprint 6 (Phase 1) — Per-tool fallback headline + icon for founder-mode.
//
// `tool_use.input.description` is the canonical source for humanized text.
// When it's missing (older tools, edge cases, custom skills the model
// doesn't auto-summarize), we synthesize a short verb + best-effort target
// from the inputs. The target NEVER leaks raw paths or shell commands —
// only the file basename, search-target word, or hostname.
//
// Iconography is mapped to Lucide icon names so consumers can lazy-import
// from `lucide-react` without a hard coupling here.

export type IconName =
  | 'BookOpen'        // Read
  | 'FileEdit'        // Edit / MultiEdit
  | 'FilePlus'        // Write
  | 'Wrench'          // Bash / generic command
  | 'Folder'          // Glob
  | 'Search'          // Grep
  | 'Globe'           // WebFetch / WebSearch
  | 'Bot'             // Task / Agent / subagent
  | 'ListChecks'      // TodoWrite
  | 'Notebook'        // NotebookEdit
  | 'Sparkles'        // Skill
  | 'Bell'            // PushNotification
  | 'Activity'        // Monitor (long-running background-task watcher)
  | 'Cog';            // generic / unknown

export interface FallbackHeadline {
  iconName: IconName;
  headline: string;
}

function basename(path: string): string {
  const trimmed = path.trim();
  if (trimmed.length === 0) return '';
  const parts = trimmed.split('/').filter(Boolean);
  return parts[parts.length - 1] ?? '';
}

function asString(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null;
}

function host(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url.replace(/^https?:\/\//, '').split('/')[0] ?? url;
  }
}

export function fallbackHeadline(
  rawTool: string,
  input: Record<string, unknown>,
): FallbackHeadline {
  switch (rawTool) {
    case 'Read': {
      const fp = asString(input.file_path);
      const target = fp ? basename(fp) : 'a file';
      return { iconName: 'BookOpen', headline: `Opened ${target}` };
    }
    case 'Edit':
    case 'MultiEdit': {
      const fp = asString(input.file_path);
      const target = fp ? basename(fp) : 'a file';
      return { iconName: 'FileEdit', headline: `Edited ${target}` };
    }
    case 'Write': {
      const fp = asString(input.file_path);
      const target = fp ? basename(fp) : 'a file';
      return { iconName: 'FilePlus', headline: `Wrote ${target}` };
    }
    case 'Bash':
      // Never include the raw command — the whole point of the founder layer.
      return { iconName: 'Wrench', headline: 'Ran command' };
    case 'Glob':
      return { iconName: 'Folder', headline: 'Listed files' };
    case 'Grep': {
      const p = asString(input.pattern);
      // Pattern often is a literal word like "TODO" or a small regex —
      // safe to surface, capped to a short length.
      const safe = p && p.length <= 40 ? p : null;
      return {
        iconName: 'Search',
        headline: safe ? `Searched for "${safe}"` : 'Searched the codebase',
      };
    }
    case 'WebFetch': {
      const u = asString(input.url);
      const h = u ? host(u) : null;
      return {
        iconName: 'Globe',
        headline: h ? `Fetched ${h}` : 'Fetched a page',
      };
    }
    case 'WebSearch': {
      const q = asString(input.query);
      const safe = q && q.length <= 60 ? q : null;
      return {
        iconName: 'Globe',
        headline: safe ? `Searched the web: "${safe}"` : 'Searched the web',
      };
    }
    case 'Task':
    case 'Agent': {
      const desc = asString(input.description) ?? asString(input.subagent_type);
      return {
        iconName: 'Bot',
        headline: desc ? `Ran subagent: ${desc}` : 'Ran subagent',
      };
    }
    case 'TodoWrite':
      return { iconName: 'ListChecks', headline: 'Updated plan' };
    case 'NotebookEdit': {
      const fp = asString(input.notebook_path);
      const target = fp ? basename(fp) : 'a notebook';
      return { iconName: 'Notebook', headline: `Edited ${target}` };
    }
    case 'Skill':
      return { iconName: 'Sparkles', headline: 'Invoked a skill' };
    case 'PushNotification':
      return { iconName: 'Bell', headline: 'Sent a notification' };
    case 'Monitor':
      // Long-running background-task watcher. The model usually populates
      // `input.description` (e.g. "Auto-merge re-enabled. Waiting CI") and
      // that wins in the founder layer; this fallback is the safety net.
      return { iconName: 'Activity', headline: 'Monitoring background task' };
    default: {
      // Generic — title-case the tool name so a CRM-side custom tool
      // ("RemoteTrigger", "ScheduleWakeup") still reads OK.
      const titled = rawTool
        .replace(/([A-Z])/g, ' $1')
        .trim()
        .replace(/^./, (c) => c.toUpperCase());
      return { iconName: 'Cog', headline: titled || 'Tool call' };
    }
  }
}
